import { useEffect, useState } from "react";

import { OptimizedImageObject } from "@/types/image.types";

interface UseImageWithFallbackOptions {
  preferredSize?: "mobile" | "tablet" | "desktop" | "large";
}

interface UseImageWithFallbackResult {
  src: string;
  isLoading: boolean;
  hasFailed: boolean;
  currentSize: string;
}

/**
 * Простой хук для обработки fallback изображений
 */
export function useImageWithFallback(
  image: OptimizedImageObject | string | null | undefined,
  options: UseImageWithFallbackOptions = {},
): UseImageWithFallbackResult {
  const { preferredSize = "tablet" } = options;

  const [currentSrc, setCurrentSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [currentSize, setCurrentSize] = useState<string>("");

  useEffect(() => {
    if (!image) {
      setHasFailed(true);
      setIsLoading(false);
      setCurrentSrc("");
      setCurrentSize("");
      return;
    }

    // Handle string URLs directly
    if (typeof image === "string") {
      setCurrentSrc(image);
      setCurrentSize("original");
      setIsLoading(false);
      setHasFailed(false);
      return;
    }

    // For OptimizedImageObject, try in order: preferred -> original
    const fallbackUrls: string[] = [];

    if (preferredSize === "tablet" && image.tablet) {
      fallbackUrls.push(image.tablet);
    } else if (preferredSize === "mobile" && image.mobile) {
      fallbackUrls.push(image.mobile);
    } else if (preferredSize === "desktop" && image.desktop) {
      fallbackUrls.push(image.desktop);
    } else if (preferredSize === "large" && image.large) {
      fallbackUrls.push(image.large);
    }

    // Always add original as fallback
    if (image.original) {
      fallbackUrls.push(image.original);
    }

    // Add other sizes as fallbacks
    if (image.desktop && !fallbackUrls.includes(image.desktop)) {
      fallbackUrls.push(image.desktop);
    }
    if (image.tablet && !fallbackUrls.includes(image.tablet)) {
      fallbackUrls.push(image.tablet);
    }
    if (image.mobile && !fallbackUrls.includes(image.mobile)) {
      fallbackUrls.push(image.mobile);
    }

    if (fallbackUrls.length === 0) {
      setHasFailed(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasFailed(false);

    // Try first URL, if it fails, try original
    const tryLoadImage = async (url: string): Promise<boolean> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });
    };

    const loadFallbackImage = async () => {
      for (const url of fallbackUrls) {
        const success = await tryLoadImage(url);
        if (success) {
          setCurrentSrc(url);
          setCurrentSize(url === image.original ? "original" : preferredSize);
          setHasFailed(false);
          setIsLoading(false);
          return;
        }
      }

      // If all failed
      setHasFailed(true);
      setIsLoading(false);
    };

    loadFallbackImage();
  }, [image, preferredSize]);

  return {
    src: currentSrc,
    isLoading,
    hasFailed,
    currentSize,
  };
}

/**
 * Hook specifically for card images (optimized for 640px with fallback)
 */
export function useCardImageWithFallback(
  image: OptimizedImageObject | string | null | undefined,
): UseImageWithFallbackResult {
  return useImageWithFallback(image, {
    preferredSize: "tablet",
  });
}

/**
 * Hook specifically for thumbnail images (optimized for 320px with fallback)
 */
export function useThumbnailImageWithFallback(
  image: OptimizedImageObject | string | null | undefined,
): UseImageWithFallbackResult {
  return useImageWithFallback(image, {
    preferredSize: "mobile",
  });
}

/**
 * Hook specifically for gallery/large images (optimized for large sizes with fallback)
 */
export function useGalleryImageWithFallback(
  image: OptimizedImageObject | string | null | undefined,
): UseImageWithFallbackResult {
  return useImageWithFallback(image, {
    preferredSize: "large",
  });
}
